import React, { useState, useCallback } from "react";
import FinancialCategorySidebar from "./components/FinancialCategorySidebar";
import FinancialCategoryEditor from "./components/FinancialCategoryEditor";
import { useFinancialCategories } from "./hooks/useFinancialCategories";
import { financialCategoryDefaults } from "../../../models/financialCategory";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * FinancialCategoriesPage (Plano de Contas / DRE)
 * Layout Master-Detail: Listagem lateral + editor de categorias.
 */
const FinancialCategoriesPage = () => {
    const { categories, loading, isSaving, actions } = useFinancialCategories();
    const [selectedCategory, setSelectedCategory] = useState(financialCategoryDefaults);
    const [isDirty, setIsDirty] = useState(false);

    const handleSelectCategory = useCallback(async (category) => {
        if (selectedCategory?.id === category.id) return;

        const isActuallyDirty = isDirty && (selectedCategory?.id || selectedCategory?.name?.trim() !== "" || false);

        if (isActuallyDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedCategory(category);
        setIsDirty(false);
    }, [selectedCategory, isDirty]);

    const handleCreateNew = useCallback(async () => {
        const isActuallyDirty = isDirty && (selectedCategory?.id || selectedCategory?.name?.trim() !== "" || false);

        if (isActuallyDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar nova categoria?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedCategory(financialCategoryDefaults);
        setIsDirty(false);
    }, [isDirty, selectedCategory]);

    const handleSave = useCallback(async (data) => {
        const success = await actions.saveCategory(data);
        if (success) {
            setIsDirty(false);
        }
    }, [actions]);

    const handleDelete = useCallback(async (id) => {
        const success = await actions.deleteCategory(id);
        if (success) {
            setSelectedCategory(financialCategoryDefaults);
            setIsDirty(false);
        }
    }, [actions]);

    const handleMarkDirty = useCallback((dirty) => setIsDirty(dirty), []);

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Plano de Contas" />
            </div>

            <div className="border-top mb-3"></div>
            <MasterDetailLayout
                master={
                    <FinancialCategorySidebar
                        categories={categories}
                        activeId={selectedCategory?.id}
                        onSelect={handleSelectCategory}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <FinancialCategoryEditor
                        category={selectedCategory}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        isSaving={isSaving}
                        onChange={handleMarkDirty}
                    />
                }
            />
        </React.Fragment>
    );
};

export default FinancialCategoriesPage;
